import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Button, message, Dropdown, Tooltip } from 'antd';
import { UserOutlined, LockOutlined, GlobalOutlined, MailOutlined } from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import { useLocale } from '../context/LocaleContext';
import { useTheme } from '../context/ThemeContext';
import { getStoreName } from '../utils/storage';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t, locale, setLocale } = useLocale();
  const { isDark } = useTheme();
  const [loading, setLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [savedStoreName, setSavedStoreName] = useState<string>('');
  const [lockedUntil, setLockedUntil] = useState<number>(0);

  const langMenuItems = [
    { key: 'zh', label: locale === 'zh' ? <b style={{ color: '#1a5fb4' }}>中文</b> : '中文', onClick: () => setLocale('zh') },
    { key: 'en', label: locale === 'en' ? <b style={{ color: '#1a5fb4' }}>English</b> : 'English', onClick: () => setLocale('en') },
  ];

  // 前端防刷：1分钟内10次请求锁定（localStorage持久化）
  const checkAndRecordAttempt = (): boolean => {
    const now = Date.now();
    const lockKey = 'login_lock_until';
    const attemptsKey = 'login_attempts';
    const lockUntil = parseInt(localStorage.getItem(lockKey) || '0', 10);
    if (lockUntil > now) {
      const secs = Math.ceil((lockUntil - now) / 1000);
      message.error(t(`请求过于频繁，请${secs}秒后再试`));
      setLockedUntil(lockUntil);
      return false;
    }
    const raw = localStorage.getItem(attemptsKey);
    let attempts: number[] = raw ? JSON.parse(raw) : [];
    attempts = attempts.filter((t) => now - t < 60000);
    attempts.push(now);
    localStorage.setItem(attemptsKey, JSON.stringify(attempts));
    if (attempts.length >= 10) {
      const until = now + 60000;
      localStorage.setItem(lockKey, String(until));
      localStorage.setItem(attemptsKey, '[]');
      message.error(t('请求过于频繁，请60秒后再试'));
      setLockedUntil(until);
      return false;
    }
    return true;
  };

  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 50);
    setSavedStoreName(getStoreName() || '');
    // 检查是否仍处于锁定期
    const lockUntil = parseInt(localStorage.getItem('login_lock_until') || '0', 10);
    if (lockUntil > Date.now()) setLockedUntil(lockUntil);
    return () => clearTimeout(timer);
  }, []);

  const onFinish = async (values: { username: string; password: string }) => {
    if (!checkAndRecordAttempt()) return;
    setLoading(true);
    try {
      await login(values.username, values.password);
      message.success(t('登录成功'));
      navigate('/');
    } catch (e: any) {
      const msg = e?.responseData?.message || '';
      if (msg) {
        const translated = t(msg);
        message.error(translated !== msg ? translated : msg);
      } else {
        message.error(t('登录失败，请检查用户名和密码'));
      }
    } finally {
      setLoading(false);
    }
  };

  const brandBg = 'linear-gradient(160deg, #0a2540 0%, #0d3a73 40%, #1a5fb4 100%)';
  const BRAND_NAME = savedStoreName || 'AGI'; // 优先显示上次登录的门店名称
  const cardBg = isDark ? '#1a1a2e' : '#ffffff';
  const pageBg = isDark ? '#0f0f1a' : '#f0f4f8';
  const textPrimary = isDark ? '#ffffff' : '#1a1a2e';
  const textSecondary = isDark ? 'rgba(255,255,255,0.5)' : '#6b7280';
  const inputBg = isDark ? 'rgba(255,255,255,0.05)' : '#f8fafc';
  const inputBorder = isDark ? 'rgba(255,255,255,0.1)' : '#e5e7eb';

  return (
    <div
      style={{
        height: '100vh',
        overflow: 'hidden',
        display: 'flex',
        background: pageBg,
        transition: 'background 0.3s ease',
      }}
    >
      {/* 左侧品牌区 - 桌面端 */}
      <div
        className="desktop-only"
        style={{
          flex: 1,
          background: brandBg,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          padding: '60px 48px',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* 装饰光晕 */}
        <div
          style={{
            position: 'absolute',
            top: '-20%',
            left: '-10%',
            width: 500,
            height: 500,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,255,255,0.12) 0%, transparent 70%)',
            filter: 'blur(60px)',
            pointerEvents: 'none',
          }}
        />
        <div
          style={{
            position: 'absolute',
            bottom: '-15%',
            right: '-5%',
            width: 400,
            height: 400,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(26,95,180,0.4) 0%, transparent 70%)',
            filter: 'blur(80px)',
            pointerEvents: 'none',
          }}
        />

        {/* 顶部品牌 */}
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div
            style={{
              width: 72,
              height: 72,
              borderRadius: 20,
              background: 'rgba(255,255,255,0.12)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255,255,255,0.18)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 40,
              boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
            }}
          >
            <span style={{ fontSize: 28, fontWeight: 800, color: '#fff', letterSpacing: 1 }}>AGI</span>
          </div>
          <h1
            style={{
              color: '#fff',
              fontSize: 36,
              fontWeight: 700,
              margin: '0 0 12px 0',
              letterSpacing: 1,
            }}
          >
            {BRAND_NAME} OMS
          </h1>

          {/* 分隔线 */}
          <div
            style={{
              width: 40,
              height: 3,
              background: 'linear-gradient(90deg, #fff, transparent)',
              borderRadius: 2,
              marginTop: 32,
            }}
          />

          {/* 特性标签 */}
          <div style={{ marginTop: 40, display: 'flex', flexDirection: 'column', gap: 16 }}>
            {[
              { label: 'Smart Orders, Zero Friction.', desc: 'Experience up to 10x faster order processing with our AI-native platform.' },
              { label: 'Automate Your Orders, Elevate Your Business.', desc: 'Let AI handle the heavy lifting—from entry to fulfillment, with zero errors.' },
              { label: 'Never Miss a Beat with AI-Powered Ordering.', desc: 'Process, track, and manage orders globally 24/7 with instant AI insights.' },
            ].map((item, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.6)',
                    marginTop: 6,
                    flexShrink: 0,
                  }}
                />
                <div>
                  <div style={{ color: '#fff', fontSize: 14, fontWeight: 500 }}>{item.label}</div>
                  <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, marginTop: 2 }}>{item.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 底部装饰 */}
        <div style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ display: 'flex', gap: 6 }}>
            {[0.5, 0.3, 0.15].map((opacity, i) => (
              <div
                key={i}
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: `rgba(255,255,255,${opacity})`,
                }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* 右侧表单区 */}
      <div
        style={{
          width: '100%',
          maxWidth: 520,
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px 24px',
          position: 'relative',
        }}
      >
        {/* 右上角语言切换 */}
        <div style={{ position: 'absolute', top: 20, right: 24, zIndex: 10 }}>
          <Dropdown menu={{ items: langMenuItems }} placement="bottomRight">
            <GlobalOutlined style={{ fontSize: 20, color: isDark ? '#aaa' : '#888', cursor: 'pointer' }} />
          </Dropdown>
        </div>
        {/* 表单卡片 */}
        <div
          style={{
            width: '100%',
            maxWidth: 400,
            background: cardBg,
            borderRadius: 24,
            padding: '48px 40px',
            boxShadow: isDark
              ? '0 20px 60px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.05)'
              : '0 20px 60px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.02)',
            opacity: mounted ? 1 : 0,
            transform: mounted ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        >
          {/* 移动端品牌标识 */}
          <div className="mobile-only" style={{ textAlign: 'center', marginBottom: 32 }}>
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: 16,
                background: 'linear-gradient(135deg, #1a5fb4, #0d3a73)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
              }}
            >
              <span style={{ fontSize: 20, fontWeight: 700, color: '#fff' }}>AGI</span>
            </div>
            <h2 style={{ color: textPrimary, margin: 0, fontSize: 22, fontWeight: 700 }}>{BRAND_NAME} OMS</h2>
          </div>

          {/* 欢迎文案 */}
          <div style={{ marginBottom: 32 }}>
            <h2
              style={{
                color: textPrimary,
                fontSize: 28,
                fontWeight: 700,
                margin: '0 0 8px 0',
              }}
            >
              {t('欢迎回来')}
            </h2>
            <p style={{ color: textSecondary, fontSize: 14, margin: 0 }}>{t('请登录您的门店账户')}</p>
          </div>

          <Form name="login" onFinish={onFinish} autoComplete="off">
            <Form.Item
              name="username"
              rules={[{ required: true, message: t('请输入用户名') }]}
              style={{ marginBottom: 20 }}
            >
              <Input
                prefix={
                  <UserOutlined style={{ color: isDark ? 'rgba(255,255,255,0.3)' : '#9ca3af', fontSize: 16 }} />
                }
                placeholder={t('用户名')}
                size="large"
                style={{
                  height: 48,
                  background: inputBg,
                  border: `1px solid ${inputBorder}`,
                  borderRadius: 12,
                  color: textPrimary,
                  fontSize: 14,
                  transition: 'all 0.2s ease',
                }}
              />
            </Form.Item>

            <Form.Item
              name="password"
              rules={[{ required: true, message: t('请输入密码') }]}
              style={{ marginBottom: 24 }}
            >
              <Input.Password
                prefix={
                  <LockOutlined style={{ color: isDark ? 'rgba(255,255,255,0.3)' : '#9ca3af', fontSize: 16 }} />
                }
                placeholder={t('密码')}
                size="large"
                style={{
                  height: 48,
                  background: inputBg,
                  border: `1px solid ${inputBorder}`,
                  borderRadius: 12,
                  color: textPrimary,
                  fontSize: 14,
                  transition: 'all 0.2s ease',
                }}
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0 }}>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                block
                loading={loading}
                disabled={lockedUntil > Date.now()}
                style={{
                  height: 48,
                  borderRadius: 12,
                  background: 'linear-gradient(135deg, #1a5fb4 0%, #0d3a73 100%)',
                  border: 'none',
                  fontSize: 15,
                  fontWeight: 600,
                  boxShadow: '0 4px 16px rgba(26,95,180,0.35)',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  (e.target as HTMLElement).style.transform = 'translateY(-1px)';
                  (e.target as HTMLElement).style.boxShadow = '0 6px 20px rgba(26,95,180,0.45)';
                }}
                onMouseLeave={(e) => {
                  (e.target as HTMLElement).style.transform = 'translateY(0)';
                  (e.target as HTMLElement).style.boxShadow = '0 4px 16px rgba(26,95,180,0.35)';
                }}
              >
                {t('登录')}
              </Button>
            </Form.Item>
          </Form>

          <div style={{ textAlign: 'center', marginTop: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <span style={{ color: textSecondary, fontSize: 13 }}>{t('还没有账号？')}</span>
            <Button type="link" size="small" onClick={() => navigate('/register')} style={{ padding: '0 4px', fontSize: 13 }}>
              {t('注册账号')}
            </Button>
            <Tooltip title="Contact US">
              <a href="mailto:info@jdsuit.com" style={{ display: 'inline-flex', alignItems: 'center', color: isDark ? '#aaa' : '#8c8c8c' }}>
                <MailOutlined style={{ fontSize: 16 }} />
              </a>
            </Tooltip>
          </div>


        </div>
      </div>
      {locale === 'zh' && (
        <div style={{ position: 'fixed', bottom: 16, left: 24, fontSize: 11, color: '#bbb', pointerEvents: 'none', userSelect: 'none' }}>
          沪ICP备2026036452号
        </div>
      )}
    </div>
  );
};

export default Login;
